import Skeleton from '@material-ui/lab/Skeleton';
import classNames from 'clsx';
import React from 'react';
import { useSelector } from 'react-redux';

import { FeatureFlag, Size } from '@swf/core/enums';
import useFeature from '@swf/core/hooks/useFeature';
import useScreenSize from '@swf/core/hooks/useScreenSize';
import { select } from '@swf/core/store';
import type { HGPokerGameTile } from '@swf/core/types/Poker';
import { GameType, UserClubRole } from '@swf/core/types/Poker';
import { Image, useHomeGamesWidget, useWpGameUrl } from '@swf/poker';

import Unsupported from '../PokerGameTile/Unsupported/Unsupported';
import Wrapper from '../PokerGameTile/Wrapper/Wrapper';

import Footer from './Footer/Footer';
import useArrangeAttributes from './Footer/useArrangeAttributes';
import styles from './GameTile.scss';
import Tags from './Tags/Tags';

const GameTile: React.FC<HGPokerGameTile> = ({
  imageUrl,
  tags,
  link,
  attributes,
  server: serverAddress,
  serverObj: serverObjectName,
  type,
  id,
  clubId,
  supported,
  name,
  maxPlayers
}) => {
  const { widgetSelect } = useHomeGamesWidget();
  const clubs = useSelector(widgetSelect.clubs);
  const isMobile = useSelector(select.device.isMobile);
  const { greaterThan } = useScreenSize();
  const isSmallDevice = !greaterThan(Size.XXSmall);
  const roleForUser = clubs.find((club) => club.clubId === Number(clubId))?.roleForUser || -1;
  const { topSectionItems } = useArrangeAttributes(attributes, Number(maxPlayers));
  const [startTime] = topSectionItems;
  const canSelect =
    (roleForUser === UserClubRole.Admin || roleForUser === UserClubRole.Manager) &&
    (type === GameType[GameType['Cash Game']] || type === GameType[GameType['Tournament']]);
  const { url } = useWpGameUrl({
    type,
    serverAddress,
    serverObjectName
  });
  const unsupported =
    useFeature(FeatureFlag.PKRHomeGamesGameTilesUnsupported) ||
    (typeof supported === 'boolean' && !supported);

  return (
    <Wrapper
      className={classNames(styles.container, {
        [styles.mobile]: isMobile || isSmallDevice
      })}
      url={!unsupported ? url : undefined}
      link={link}
      id={id}
      clubId={clubId}
      supported={supported}
      data-testid="game-item"
    >
      <div className={styles.imgContainer}>
        <Image
          loader={<Skeleton className={styles.loading} variant="rect" height={140} />}
          alt="Game image"
          data-testid="game-item-image"
          imageUrl={imageUrl}
        />
        {!!startTime && isSmallDevice && (
          <div className={styles.marquee}>
            <span>
              {startTime.name}: {startTime.value}
            </span>
          </div>
        )}
      </div>
      <Tags tags={tags} />
      <Footer
        canSelect={canSelect}
        id={id}
        attributes={attributes}
        tableName={name}
        maxPlayers={maxPlayers}
        isSmallDevice={isSmallDevice}
      />
      {unsupported && <Unsupported />}
    </Wrapper>
  );
};

export default GameTile;
